import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Shield, Info, CheckCircle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CookieSettings from '../components/CookieSettings';

const CookiePolicy = () => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  return (
    <>
      <Navbar />
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center text-ta-green hover:underline">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Cookie className="h-8 w-8 text-ta-green mr-3" />
                <h1 className="text-2xl font-bold text-gray-900">Cookie Policy</h1>
              </div>
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="px-4 py-2 bg-ta-green text-white rounded-md hover:bg-ta-green-dark transition-colors text-sm"
              >
                Cookie Settings
              </button>
            </div>
            
            <div className="prose max-w-none">
              <div className="text-sm text-gray-500 mb-6">
                Last Updated: June 15, 2025
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Info className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      This Cookie Policy explains how TravelCord uses cookies and similar tracking technologies when you visit our website. We respect your privacy and are committed to being transparent about the technologies we use.
                    </p>
                  </div>
                </div>
              </div>

              {/* Cookie Types Section */}
              <div className="mb-8">
                <button
                  onClick={() => toggleSection('types')}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h2 className="text-xl font-bold text-gray-900">Types of Cookies We Use</h2>
                  {expandedSections.includes('types') ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {expandedSections.includes('types') && (
                  <div className="mt-4 space-y-4 animate-fadeIn">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Essential Cookies</h3>
                      <p className="text-sm text-gray-700 mb-2">
                        Required for basic website functionality. These cookies are necessary for the website to work properly.
                      </p>
                      <ul className="list-disc pl-5 text-sm text-gray-600">
                        <li>Authentication and security</li>
                        <li>Basic website functionality</li>
                        <li>Shopping cart and payment processing</li>
                      </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Functional Cookies</h3>
                      <p className="text-sm text-gray-700 mb-2">
                        Enable enhanced functionality and personalization.
                      </p>
                      <ul className="list-disc pl-5 text-sm text-gray-600">
                        <li>Language preferences</li>
                        <li>Location-based content</li>
                        <li>Personalized recommendations</li>
                      </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Analytics Cookies</h3>
                      <p className="text-sm text-gray-700 mb-2">
                        Help us understand how visitors interact with our website.
                      </p>
                      <ul className="list-disc pl-5 text-sm text-gray-600">
                        <li>Page views and navigation</li>
                        <li>Time spent on site</li>
                        <li>Error tracking</li>
                      </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Advertising Cookies</h3>
                      <p className="text-sm text-gray-700 mb-2">
                        Used to deliver relevant advertisements and marketing campaigns.
                      </p>
                      <ul className="list-disc pl-5 text-sm text-gray-600">
                        <li>Behavioral advertising</li>
                        <li>Ad performance tracking</li>
                        <li>Campaign optimization</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* Cookie Management Section */}
              <div className="mb-8">
                <button
                  onClick={() => toggleSection('management')}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h2 className="text-xl font-bold text-gray-900">Managing Your Cookie Preferences</h2>
                  {expandedSections.includes('management') ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {expandedSections.includes('management') && (
                  <div className="mt-4 animate-fadeIn">
                    <p className="text-gray-700 mb-4">
                      You can manage your cookie preferences in several ways:
                    </p>
                    
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-medium text-gray-900 mb-2">Cookie Settings</h3>
                        <p className="text-sm text-gray-700 mb-2">
                          Use our cookie settings panel to customize which cookies you accept.
                        </p>
                        <button
                          onClick={() => setIsSettingsOpen(true)}
                          className="text-ta-green hover:underline text-sm"
                        >
                          Open Cookie Settings
                        </button>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-medium text-gray-900 mb-2">Browser Settings</h3>
                        <p className="text-sm text-gray-700">
                          Most web browsers allow you to manage cookies through their settings preferences. Here's how to access cookie settings in popular browsers:
                        </p>
                        <ul className="list-disc pl-5 mt-2 text-sm text-gray-600">
                          <li>Chrome: Settings → Privacy and Security → Cookies</li>
                          <li>Firefox: Options → Privacy & Security → Cookies</li>
                          <li>Safari: Preferences → Privacy → Cookies</li>
                          <li>Edge: Settings → Privacy & Security → Cookies</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Third-Party Cookies Section */}
              <div className="mb-8">
                <button
                  onClick={() => toggleSection('thirdParty')}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h2 className="text-xl font-bold text-gray-900">Third-Party Cookies</h2>
                  {expandedSections.includes('thirdParty') ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {expandedSections.includes('thirdParty') && (
                  <div className="mt-4 animate-fadeIn">
                    <p className="text-gray-700 mb-4">
                      We use services from third-party providers who may set their own cookies:
                    </p>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purpose</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Google Analytics</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Analytics tracking</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2 years</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Facebook Pixel</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Marketing tracking</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">180 days</td>
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Stripe</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Payment processing</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Session</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Updates Section */}
              <div className="mb-8">
                <button
                  onClick={() => toggleSection('updates')}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h2 className="text-xl font-bold text-gray-900">Updates to This Policy</h2>
                  {expandedSections.includes('updates') ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {expandedSections.includes('updates') && (
                  <div className="mt-4 animate-fadeIn">
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-yellow-700">
                            We may update this Cookie Policy from time to time. The latest version will always be available on our website. Significant changes will be notified to users through a prominent notice on our website.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Section */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Us</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about our Cookie Policy, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium text-gray-900">TravelCord</p>
                  <p className="text-gray-700">Ntinda, Kampala, Uganda</p>
                  <p className="text-gray-700">Email: privacy@travelcord.guide</p>
                  <p className="text-gray-700">Phone: +256766830194</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CookieSettings 
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      <Footer />
    </>
  );
};

export default CookiePolicy;